import { useState, type ChangeEvent, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Alert, AlertDescription } from '../ui/alert'
import {
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog'
import { Separator } from '../ui/separator'
import { supabase } from '../../lib/supabase'
import { validatePassword } from '../../utils/validate-password'
import { useTranslation } from 'react-i18next'
import { Checkbox } from '../ui/checkbox'
import { Link } from 'react-router-dom'

interface AuthFormProps {
  onSuccess?: () => void
  /** 'register' gdy okno otwiera „Generuj" — taki uzytkownik najpewniej nie ma jeszcze konta. */
  initialMode?: 'login' | 'register'
}

export function AuthForm({ onSuccess, initialMode = 'login' }: AuthFormProps) {
  const navigate = useNavigate()
  const { login, signup } = useAuth()
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [validationErrors, setValidationErrors] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isGoogleLoading, setIsGoogleLoading] = useState(false)
  const [verificationEmail, setVerificationEmail] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const registrationEnabled = import.meta.env.VITE_ENABLE_REGISTRATION === 'true'
  const [mode, setMode] = useState<'login' | 'reset' | 'register'>(
    initialMode === 'register' && registrationEnabled ? 'register' : 'login'
  )
  /*
    Akceptacja regulaminu i polityki prywatnosci. Do TASK-86 regulamin opieral sie wylacznie
    na par. 1.3 ("korzystasz = zobowiazany jestes przestrzegac") — nikt go nigdy nie zaakceptowal
    ani nawet nie zobaczyl przy zakladaniu konta. Domyslnie NIEZAZNACZONE: zgoda zaznaczona
    z gory nie jest zgoda. Wersje dokumentu zapisujemy razem z akceptacja (lib/supabase.ts),
    bo "zaakceptowal regulamin" bez wskazania wersji nie mowi, CO zaakceptowal.
  */
  const [legalAccepted, setLegalAccepted] = useState(false)


  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setError(null)
    setMessage(null)
    setValidationErrors([])

    // Validate password for register mode
    if (mode === 'register') {
      if (!legalAccepted) return
      const validation = validatePassword(password)
      if (!validation.isValid) {
        setValidationErrors(validation.errors)
        return
      }
    }

    setIsLoading(true)

    try {
      switch (mode) {
        case 'login' : {
          await login(email, password)
          onSuccess?.()
          break
        }
        case 'reset' : {
          const { error } = await supabase.auth.resetPasswordForEmail(email, {
            //redirect is configured in supabase 
            //redirectTo: `${window.location.origin}/reset-password`
          })
          if (error) throw error
          setMessage(t('check-email-reset'))
          setIsLoading(false)
          setTimeout(() => {
            onSuccess?.()
            navigate('/')
          }, 2000)
          break
        }
        case 'register' : {
          if (!registrationEnabled) {
            throw new Error(t('registration-disabled'))
          }
          await signup(email, password)
          setVerificationEmail(email)
          break;
        }
      }
    } catch (err) {
      setError(mode === 'reset' 
        ? t('reset-link-failed')
        : err instanceof Error ? err.message : 'An error occurred'
      )
      setIsLoading(false)
    } finally {
      if (mode !== 'reset') {
        setIsLoading(false)
      }
    }
  }

  const signInWithGoogle = async () => {
    setError(null)
    setMessage(null)
    setIsGoogleLoading(true)
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin,
        },
      })
      if (error) throw error
    } catch (err) {
      setError(err instanceof Error ? err.message : t('google-login-failed'))
      setIsGoogleLoading(false)
    }
  }

  const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)
  const handlePasswordChange = (e: ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value)
    // Clear validation errors when user starts typing
    if (validationErrors.length > 0) {
      setValidationErrors([])
    }
  }

  if (verificationEmail) {
    return (
      <div className="px-6 py-8 space-y-4 text-center">
        <h2 className="text-2xl font-semibold">{t('check-your-email')}</h2>
        <p className="text-muted-foreground">
          {t('verification-email-sent', { email: verificationEmail })}
        </p>
        <p className="text-sm text-muted-foreground">
          {t('after-verification')}
        </p>
      </div>
    )
  }

  function getDialogContent(mode: 'login' | 'reset' | 'register') {
    switch (mode) {
      case 'login':
        return { 
          title: t('sign-in'),
          description: '',
          buttonText: t('sign-in'),
        }
      case 'reset':
        return { 
          title: t('reset-password'), 
          description: t('reset-password-description'),
          buttonText: t('send-link'),
        } 
      case 'register':
        return { 
          title: t('register'),
          // Bez opisu: nad formularzem stoi juz tytul okna, a kazda linijka wiecej to powod, by odpasc.
          description: '',
          buttonText: t('create-account'),
        }
    }
  }

  const dialogContent = getDialogContent(mode)
  if ('title' in dialogContent) {
    const {title, description, buttonText} = dialogContent
    return (
      <form onSubmit={handleSubmit} className="space-y-6">
        {description && (
          <div className="px-6 pt-6">
            <DialogHeader>
              <DialogDescription>
                {description}
              </DialogDescription>
            </DialogHeader>
          </div>
        )}

        {/* == START: Add Google Button and Separator == */}
        {mode !== 'reset' && (
          <div className="px-6 pt-4 space-y-3">
            {/* Dwie ramki z „LUB" miedzy nimi: Google i e-mail+haslo to ALTERNATYWY. Przy jednym
                ciaglym formularzu wygladalo, jakby po Google trzeba bylo jeszcze podac haslo. */}
            <div className="rounded-lg bg-muted p-4 space-y-3" data-testid="auth-google-panel">
            {/* type="button": ten przycisk stoi w <form> przed „Zaloguj sie" — bez tego byl domyslnym
                przyciskiem formularza i Enter w polu hasla przekierowywal do Google. */}
            <Button 
              type="button"
              variant="outline" 
              className="w-full bg-background"
              onClick={signInWithGoogle}
              disabled={isGoogleLoading || isLoading}
              data-testid="google-login-button"
            >
              {isGoogleLoading ? t('processing') : mode === 'register' ? t('sign-up-with-google') : t('sign-in-with-google')}
            </Button>
            {/* Pierwsze logowanie Google ZAKLADA konto, wiec uzytkownik musi wiedziec, na co
                sie godzi, zanim kliknie — checkboxa nie da sie tu uzyc, bo ten sam przycisk
                sluzy do logowania istniejacych kont. */}
            <p className="text-xs text-muted-foreground text-center" data-testid="google-legal-note">
              {t('legal-consent-oauth')}{' '}
              <Link to="/terms" target="_blank" className="underline hover:text-foreground">{t('legal-consent-terms')}</Link>
              {' '}{t('legal-consent-and')}{' '}
              <Link to="/privacy" target="_blank" className="underline hover:text-foreground">{t('legal-consent-privacy')}</Link>.
            </p>
            </div>
            <div className="flex items-center gap-3" aria-hidden="true">
              <Separator className="flex-1" />
              <span className="rounded-full border px-3 py-0.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t('or')}
              </span>
              <Separator className="flex-1" />
            </div>
          </div>
        )}
        {/* == END: Add Google Button and Separator == */}

        <div className="px-6">
        <div className={mode !== 'reset' ? 'rounded-lg bg-muted p-4 space-y-4' : 'space-y-4'} data-testid="auth-email-panel">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {message && (
            <Alert>
              <AlertDescription>{message}</AlertDescription>
            </Alert>
          )}

          {validationErrors.length > 0 && (
            <Alert variant="destructive">
              <AlertDescription>
                <ul className="list-disc pl-4">
                  {validationErrors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="email">{t('email')}</Label>
            <Input
              id="email"
              type="email"
              data-testid="auth-email-input"
              value={email}
              onChange={handleEmailChange}
              required
            />
          </div>

          {mode !== 'reset' && (
            <div className="space-y-2">
              <Label htmlFor="password">{t('password')}</Label>
              <Input
                id="password"
                type="password"
                data-testid="auth-password-input"
                value={password}
                onChange={handlePasswordChange}
                required
              />
              {mode === 'register' && (
                <p className="text-xs text-muted-foreground mt-1">
                  {t('password-requirements')}
                </p>
              )}
            </div>
          )}

          {mode === 'register' && (
            <div className="flex items-start gap-2">
              <Checkbox
                id="legal-consent"
                checked={legalAccepted}
                onCheckedChange={(checked) => setLegalAccepted(checked === true)}
                disabled={isLoading}
                data-testid="legal-consent"
              />
              <Label htmlFor="legal-consent" className="text-xs font-normal leading-snug text-muted-foreground">
                {t('legal-consent-prefix')}{' '}
                <Link to="/terms" target="_blank" className="underline hover:text-foreground">{t('legal-consent-terms')}</Link>
                {' '}{t('legal-consent-and')}{' '}
                <Link to="/privacy" target="_blank" className="underline hover:text-foreground">{t('legal-consent-privacy')}</Link>.
              </Label>
            </div>
          )}

          {mode !== 'reset' && (
            <div className="flex flex-col gap-1">
              <Button
                type="submit"
                className="w-full"
                disabled={isLoading || (mode === 'register' && !legalAccepted)}
                data-testid="auth-submit-button"
              >
                {isLoading ? t('processing') : buttonText}
              </Button>
              {mode === 'login' && <Button
                type="button"
                variant="link"
                className="w-full h-auto py-1"
                onClick={() => {
                  setMode('reset')
                  setError(null)
                  setMessage(null)
                }}
                disabled={isLoading}
              >
                {t('forgot-password')}
              </Button>}
            </div>
          )}
        </div>
        </div>


        <DialogFooter className="px-6 pb-6">
          <div className="flex flex-col gap-2 w-full">
            {mode === 'reset' && (
              <Button 
                type="submit" 
                className="w-full"
                disabled={isLoading}
                data-testid="auth-submit-button"
              >
                {isLoading ? t('processing') : buttonText}
              </Button>
            )}

            {mode === 'reset' && (
              <Button
                type="button"
                variant="link"
                className="w-full"
                onClick={() => {
                  setMode('login')
                  setError(null)
                  setMessage(null)
                }}
                disabled={isLoading}
              >
                {t('back-to-login')}
              </Button>
            )}

            {mode !== 'reset' && (
              <Button
                type="button"
                variant="link"
                className="w-full font-semibold"
                onClick={() => setMode(mode === 'login' ? 'register' : 'login')}
                disabled={isLoading}
                data-testid="auth-mode-toggle"
              >
                {mode === 'login' 
                  ? t('no-account-register')
                  : t('have-account-login')
                }
              </Button>
            )}
          </div>
        </DialogFooter>
      </form>
    )
  } else {
    return dialogContent
  }
} 
